import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useI18n } from '../lib/i18n';
import { useNavigate } from 'react-router-dom';
import { UsersThree, ChatsCircle } from '@phosphor-icons/react';
import { DegreeAudit } from '../components/acad/DegreeAudit';

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const Sel = (props) => <select {...props} className={`border border-slate-200 rounded-md px-2 py-2 text-sm bg-white ${props.className || ''}`} />;
const STANDING_STYLE = { 'Good standing': 'bg-green-100 text-green-700', Warning: 'bg-amber-100 text-amber-700', Probation: 'bg-rose-100 text-rose-700', Dismissed: 'bg-slate-200 text-slate-600', Honours: 'bg-orange-100 text-orange-700' };

const StudentDetail = ({ rec, standings, onChanged }) => {
  const { data: regs } = useQuery({ queryKey: ['adv-regs', rec.student_id], queryFn: () => api.get('/acad/registrations', { params: { student_id: rec.student_id } }).then((r) => r.data) });
  const { data: grades } = useQuery({ queryKey: ['adv-grades', rec.student_id], queryFn: () => api.get('/acad/grades', { params: { student_id: rec.student_id } }).then((r) => r.data) });
  const [standing, setStanding] = useState(rec.academic_standing || 'Good standing');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const change = async (patch, why) => {
    try {
      await api.post(`/acad/student-programmes/${rec.id}/change`, { ...patch, reason: why });
      toast.success('Record updated - change appended to history'); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-3 grid lg:grid-cols-2 gap-4" data-testid={`adv-detail-${rec.id}`}>
      <div className="text-sm">
        <p className="font-semibold text-xs uppercase text-slate-400 mb-1">This term's registrations</p>
        {(regs || []).filter((r) => r.status !== 'dropped').map((r) => (
          <p key={r.id} className="py-0.5 text-xs">{r.course_code} {r.section_code} · {r.course_title} · <span className={r.status === 'waitlisted' ? 'text-amber-600' : 'text-green-700'}>{r.status}{r.waitlist_position ? ` #${r.waitlist_position}` : ''}</span></p>
        ))}
        {(regs || []).filter((r) => r.status !== 'dropped').length === 0 && <p className="text-xs text-slate-400">No active registrations.</p>}
        <p className="font-semibold text-xs uppercase text-slate-400 mb-1 mt-3">Official grades</p>
        {(grades || []).filter((g) => g.status === 'official').map((g) => (
          <p key={g.id} className="py-0.5 text-xs">{g.course_code} · {String(g.value)} · {g.credit_value} cr {g.passed ? '' : '· not passed'}</p>
        ))}
        {(grades || []).filter((g) => g.status === 'official').length === 0 && <p className="text-xs text-slate-400">No official grades yet.</p>}
        <p className="font-semibold text-xs uppercase text-slate-400 mb-1 mt-3">Degree audit</p>
        <DegreeAudit studentId={rec.student_id} />
      </div>
      <div>
        <p className="font-semibold text-xs uppercase text-slate-400 mb-1">Change standing</p>
        <div className="flex gap-2 flex-wrap items-center">
          <Sel data-testid={`adv-standing-${rec.id}`} value={standing} onChange={(e) => setStanding(e.target.value)}>{standings.map((s) => <option key={s}>{s}</option>)}</Sel>
          <Input data-testid={`adv-standing-reason-${rec.id}`} className="w-52" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button data-testid={`adv-standing-save-${rec.id}`} size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!reason.trim() || standing === rec.academic_standing}
            onClick={() => change({ academic_standing: standing }, reason)}>Update standing</Button>
        </div>
        <p className="font-semibold text-xs uppercase text-slate-400 mb-1 mt-3">Add a note to the record</p>
        <div className="flex gap-2 items-center">
          <Input data-testid={`adv-note-${rec.id}`} placeholder="Advising note" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button data-testid={`adv-note-save-${rec.id}`} size="sm" variant="outline" disabled={!note.trim()}
            onClick={() => { change({ note }, 'Advising note'); setNote(''); }}>Save note</Button>
        </div>
        <p className="font-semibold text-xs uppercase text-slate-400 mb-1 mt-3">History</p>
        <div className="max-h-32 overflow-auto">
          {(rec.history || []).slice().reverse().map((h) => (
            <p key={h.at} className="text-xs text-slate-500 py-0.5">{h.at?.slice(0, 16)} · {h.change}{h.note ? ` · "${h.note}"` : ''} · {h.by}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Advisor() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: caseload } = useQuery({ queryKey: ['adv-caseload'], queryFn: () => api.get('/acad/advisor/caseload').then((r) => r.data) });
  const { data: standingsRef } = useQuery({ queryKey: ['ref', 'academic_standings'], queryFn: () => api.get('/acad/refdata/academic_standings').then((r) => r.data) });
  const standings = (standingsRef?.values || []).filter((v) => v.active).map((v) => v.value);
  const [open, setOpen] = useState(null);
  const onChanged = () => { qc.invalidateQueries({ queryKey: ['adv-caseload'] }); };
  const message = async (studentId) => {
    try {
      await api.post('/chat/guardian-threads', { student_id: studentId });
      toast.success('Chat thread ready - taking you there');
      navigate('/chat');
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5" data-testid="advisor-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><UsersThree size={26} className="text-orange-600" />{t('Advisor workspace')}</h1>
        <p className="text-sm text-slate-500">{t('Your advisees at a glance - standings, programme progress and this term\u2019s registrations.')}</p>
      </div>
      {(caseload || []).map((r) => (
        <Card key={r.id} testid={`adv-row-${r.id}`}>
          <div className="flex items-center gap-3">
            <button data-testid={`adv-open-${r.id}`} onClick={() => setOpen(open === r.id ? null : r.id)} className="flex-1 text-start">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm">
                  <b>{r.student_name}</b> <span className="text-xs text-slate-400">{r.programme_code} {r.programme_name}</span>
                  <span className={`ms-2 text-[10px] px-1.5 py-0.5 rounded ${STANDING_STYLE[r.academic_standing] || 'bg-slate-100 text-slate-500'}`}>{r.academic_standing}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{r.active_registrations} active registration(s)</span>
                  <span>{r.credits_earned}/{r.credits_required || '-'} credits</span>
                  {r.progress_pct !== null && (
                    <span className="flex items-center gap-1.5 w-36">
                      <span className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden"><span className="block h-full bg-orange-500" style={{ width: `${Math.min(100, r.progress_pct)}%` }} /></span>
                      {r.progress_pct}%
                    </span>
                  )}
                </div>
              </div>
            </button>
            <button data-testid={`adv-message-${r.id}`} onClick={() => message(r.student_id)}
              className="text-xs border border-slate-200 rounded-md px-2.5 py-1.5 hover:bg-slate-50 flex items-center gap-1 shrink-0">
              <ChatsCircle size={14} className="text-orange-600" /> Message
            </button>
          </div>
          {open === r.id && <StudentDetail rec={r} standings={standings} onChanged={onChanged} />}
        </Card>
      ))}
      {(caseload || []).length === 0 && (
        <Card testid="adv-empty"><p className="text-sm text-slate-400">No advisees yet. A registrar assigns you as advisor on a student's academic record (Student 360, Academic record tab).</p></Card>
      )}
    </div>
  );
}
